(function () {
  'use strict';

  const durations = [1600, 2600, 3400, 2400];
  const captions = [
    'A shopper leaves their cart.',
    'myuser.ai writes and sends a reminder.',
    'The shopper asks a question. myuser.ai answers.',
    'The shopper completes their order.'
  ];
  const emailCaptions = [
    'Maya leaves her cart.',
    'myuser.ai sends Maya an email reminder.',
    'Maya opens her cart from the email.',
    'Maya completes her sample order.'
  ];

  // The hero is a complete illustration. Input adds depth; it never gates content.
  function initializeHero() {
    const board = document.querySelector('[data-hero-illustration]');
    if (!board || board.dataset.heroReady === 'true') return;
    board.dataset.heroReady = 'true';
    const stage = board.querySelector('.ch-proof-stage');
    const page = board.closest('.conversion-home');
    const hero = board.closest('.ch-hero');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let visible = !window.IntersectionObserver, away = false, frame = null;
    let bounds = null, x = 0, y = 0, sectionHeight = 0, observer, resizeObserver;
    const allowed = () => visible && !away && !document.hidden && !reduced.matches && pointer.matches;
    function reset() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null; bounds = null; x = 0; y = 0;
      board.style.setProperty('--proof-x', '0');
      board.style.setProperty('--proof-y', '0');
    }
    function sizeSection() {
      bounds = null;
      if (!hero || !page) return;
      const height = Math.ceil(hero.getBoundingClientRect().height);
      if (height > 0 && height !== sectionHeight) {
        sectionHeight = height;
        page.style.setProperty('--ch-section-min-height', `${height}px`);
      }
    }
    stage.addEventListener('pointermove', event => {
      if (!allowed() || event.pointerType === 'touch') return;
      bounds ||= stage.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const clamp = value => Math.max(-1, Math.min(1, value));
      x = clamp((event.clientX - bounds.left) / bounds.width * 2 - 1);
      y = clamp((event.clientY - bounds.top) / bounds.height * 2 - 1);
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (!allowed()) { reset(); return; }
        board.style.setProperty('--proof-x', x.toFixed(3));
        board.style.setProperty('--proof-y', y.toFixed(3));
      });
    }, { passive: true });
    stage.addEventListener('pointerleave', reset);
    stage.addEventListener('pointercancel', reset);
    reduced.addEventListener('change', reset);
    pointer.addEventListener('change', reset);
    document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });
    window.addEventListener('scroll', () => { bounds = null; }, { passive: true });
    window.addEventListener('resize', () => { reset(); sizeSection(); }, { passive: true });
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(sizeSection);
      if (hero) resizeObserver.observe(hero);
    }
    if (window.IntersectionObserver) {
      observer = new IntersectionObserver(entries => {
        const entry = entries.find(entry => entry.target === stage);
        if (!entry) return;
        visible = entry.isIntersecting && entry.intersectionRatio >= .1;
        if (visible) board.dataset.entered = 'true';
        else reset();
      }, { threshold: [0, .1] });
      observer.observe(stage);
    } else board.dataset.entered = 'true';
    window.addEventListener('pagehide', () => {
      away = true; visible = false; reset();
      observer?.disconnect(); resizeObserver?.disconnect();
    });
    window.addEventListener('pageshow', () => {
      if (!away) return;
      away = false;
      if (observer) observer.observe(stage); else visible = true;
      if (resizeObserver && hero) resizeObserver.observe(hero);
      sizeSection();
    });
    reset(); sizeSection();
  }

  function initialize() {
    initializeMobileGalleries();
    initializeHero();
    const boards = Array.from(document.querySelectorAll('[data-home-demo]'));
    if (
      !boards.length ||
      boards.every((board) => board.dataset.homeReady === 'true')
    )
      return;

    const preference = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };
    let reduced = preference.matches;
    let pageAway = false;
    let observer;
    const states = new Map();
    const page = document.querySelector('.conversion-home');
    const hero = page?.querySelector('.ch-hero');
    let sectionObserver;
    let sectionHeight = 0;
    function sizeColorSections() {
      if (!hero) return;
      const height = Math.ceil(hero.getBoundingClientRect().height);
      if (height > 0 && height !== sectionHeight) {
        sectionHeight = height;
        page.style.setProperty('--ch-section-min-height', `${height}px`);
      }
    }
    // Observe only the reference section; the bands never resize their own reference.
    // This also follows font loading and responsive wrapping without scroll work.
    if (hero && window.ResizeObserver) {
      sectionObserver = new window.ResizeObserver(sizeColorSections);
      sectionObserver.observe(hero);
    }
    sizeColorSections();

    function track(name) {
      document.dispatchEvent(
        new CustomEvent('myuser:tool-event', { detail: { name } })
      );
    }

    function render(state) {
      state.board.dataset.step = String(state.step);
      state.board.dataset.playing = String(state.timer !== null);
      state.scenes.forEach((scene) => {
        // Some scenes keep the cause visible while the next step shows its result.
        const visibleSteps = scene.dataset.demoVisibleSteps?.split(',');
        const hidden = visibleSteps
          ? !visibleSteps.includes(String(state.step))
          : state.exclusiveScenes
          ? scene.dataset.demoPersistent !== 'true' && Number(scene.dataset.demoStep) !== state.step
          : !scene.classList.contains('ch-event') && Number(scene.dataset.demoStep) > state.step;
        scene.setAttribute('aria-hidden', String(hidden));
        if (state.exclusiveScenes) scene.inert = hidden;
      });
      state.progress.forEach((button) => {
        const selected = Number(button.dataset.demoProgress) === state.step;
        button.setAttribute('aria-pressed', String(selected));
        if (selected) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      if (state.play)
        state.play.textContent = state.hasPlayed
          ? 'Replay'
          : 'Play the example';
      if (state.pause) {
        state.pause.hidden = !state.running || reduced;
        state.pause.textContent = state.userPaused ? 'Resume' : 'Pause';
        state.pause.setAttribute(
          'aria-label',
          state.userPaused ? 'Resume the example' : 'Pause the example'
        );
        state.pause.setAttribute('aria-pressed', String(state.userPaused));
      }
      if (state.status) {
        state.status.setAttribute(
          'aria-live',
          state.announce ? 'polite' : 'off'
        );
        let caption = captions[state.step];
        if (state.board.dataset.demoChannel === 'email') {
          if (state.exclusiveScenes) caption = emailCaptions[state.step];
          else if (state.step === 2) caption = 'Maya clicks the button and opens her cart.';
        }
        if (state.board.dataset.demoChannel === 'shopper') {
          caption = state.scenes.find(scene => Number(scene.dataset.demoStep) === state.step && scene.dataset.demoCaption)?.dataset.demoCaption || '';
        }
        if (state.status.textContent !== caption)
          state.status.textContent = caption;
      }
    }

    function suspend(state) {
      if (state.timer !== null) {
        window.clearTimeout(state.timer);
        state.timer = null;
        state.remaining = Math.max(
          0,
          state.remaining - (window.performance.now() - state.startedAt)
        );
      }
      render(state);
    }

    function finish(state) {
      suspend(state);
      state.running = false;
      state.userPaused = false;
      state.step = 3;
      state.remaining = 0;
      if (state.manualRun) track('homepage_demo_completed');
      state.manualRun = false;
      render(state);
    }

    function schedule(state) {
      if (
        !state.running ||
        state.userPaused ||
        reduced ||
        !state.visible ||
        document.hidden ||
        pageAway ||
        state.timer !== null
      ) {
        render(state);
        return;
      }
      state.startedAt = window.performance.now();
      state.timer = window.setTimeout(() => {
        state.timer = null;
        if (state.step === 3) {
          finish(state);
          return;
        }
        state.step += 1;
        state.remaining = state.durations[state.step];
        schedule(state);
      }, state.remaining);
      render(state);
    }

    function start(state, manual) {
      suspend(state);
      state.autoPlayed = true;
      state.hasPlayed = true;
      state.running = true;
      state.userPaused = false;
      state.manualRun = manual;
      state.announce = manual;
      state.step = 0;
      state.remaining = state.durations[0];
      if (manual) track('homepage_demo_started');
      if (reduced) finish(state);
      else schedule(state);
    }

    function sync(state) {
      if (!state.visible || document.hidden || pageAway || reduced) {
        suspend(state);
        return;
      }
      if (!state.autoPlayed) start(state, false);
      else schedule(state);
    }

    function selectStep(state, step) {
      if (!Number.isInteger(step) || step < 0 || step > 3) return false;
      suspend(state);
      state.autoPlayed = true;
      state.running = false;
      state.manualRun = false;
      state.announce = true;
      state.userPaused = false;
      state.step = step;
      render(state);
      return true;
    }

    boards.forEach((board) => {
      if (board.dataset.homeReady === 'true') return;
      board.dataset.homeReady = 'true';
      const initialStep = /^[0-3]$/.test(board.dataset.demoInitialStep || '')
        ? Number(board.dataset.demoInitialStep)
        : 3;
      const state = {
        board,
        durations: board.dataset.demoDuration === '6000' ? [6000, 6000, 6000, 6000] : durations,
        play: board.querySelector('[data-demo-play]'),
        pause: board.querySelector('[data-demo-pause]'),
        status: board.querySelector('[data-demo-status]'),
        progress: Array.from(board.querySelectorAll('[data-demo-progress]')),
        scenes: Array.from(board.querySelectorAll('[data-demo-step]')),
        exclusiveScenes: board.dataset.demoLayout === 'scenes',
        step: initialStep,
        timer: null,
        remaining: 0,
        startedAt: 0,
        visible: !window.IntersectionObserver,
        running: false,
        userPaused: false,
        autoPlayed: reduced || board.dataset.homeAutoplay === 'false',
        hasPlayed: false,
        manualRun: false,
        announce: false
      };
      states.set(board, state);
      if (state.play)
        state.play.addEventListener('click', () => start(state, true));
      if (state.pause)
        state.pause.addEventListener('click', () => {
          if (!state.running) return;
          state.userPaused = !state.userPaused;
          if (state.userPaused) suspend(state);
          else schedule(state);
        });
      if (state.exclusiveScenes)
        board.addEventListener('focusin', (event) => {
          if (!state.running || state.userPaused) return;
          const control = event.target;
          if (!control.matches('a[href], button, input, select, textarea, [contenteditable="true"], [tabindex]:not([tabindex="-1"])')) return;
          const scene = control.closest('[data-demo-step]');
          if (!scene || Number(scene.dataset.demoStep) !== state.step) return;
          // Keep a focused action on screen until the visitor chooses to move on.
          state.userPaused = true;
          suspend(state);
        });
      state.progress.forEach((button) =>
        button.addEventListener('click', () => {
          selectStep(state, Number(button.dataset.demoProgress));
        })
      );
      board.querySelectorAll('[data-demo-go]').forEach((button) =>
        button.addEventListener('click', () => {
          if (!selectStep(state, Number(button.dataset.demoGo))) return;
          if (state.exclusiveScenes) {
            const scene = state.scenes.find(panel => Number(panel.dataset.demoStep) === state.step);
            scene?.focus({ preventScroll: true });
          }
        })
      );
      render(state);
    });

    function observe() {
      if (window.IntersectionObserver) {
        if (!observer)
          observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const state = states.get(entry.target);
                if (!state) return;
                state.visible =
                  entry.isIntersecting && entry.intersectionRatio >= 0.25;
                sync(state);
              });
            },
            { threshold: [0, 0.25] }
          );
        states.forEach((state) => {
          state.visible = false;
          observer.observe(state.board);
        });
      } else
        states.forEach((state) => {
          state.visible = true;
          sync(state);
        });
    }

    document.querySelectorAll('[data-open-example]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;
        const target = document.getElementById(href.slice(1));
        const state = states.get(target);
        if (!state) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: 'start'
        });
        target.focus({ preventScroll: true });
        start(state, true);
      });
    });

    document.addEventListener('visibilitychange', () => states.forEach(sync));
    const onPreferenceChange = (event) => {
      reduced = event.matches;
      states.forEach((state) => {
        if (reduced) {
          // Changing a device preference is not a completed marketing interaction.
          state.manualRun = false;
          state.announce = false;
          state.autoPlayed = true;
          if (state.board.dataset.demoChannel === 'shopper') suspend(state);
          else finish(state);
        } else sync(state);
      });
    };
    if (preference.addEventListener)
      preference.addEventListener('change', onPreferenceChange);
    else if (preference.addListener) preference.addListener(onPreferenceChange);

    window.addEventListener('pagehide', () => {
      pageAway = true;
      states.forEach(suspend);
      if (observer) observer.disconnect();
      if (sectionObserver) sectionObserver.disconnect();
    });
    window.addEventListener('pageshow', () => {
      if (!pageAway) return;
      pageAway = false;
      if (sectionObserver) sectionObserver.observe(hero);
      sizeColorSections();
      observe();
    });
    observe();
  }

  // Native scrolling keeps swipe and keyboard navigation usable without JS.
  // Controls follow the visible card; nothing advances on a timer.
  function initializeMobileGalleries() {
    const mobile = window.matchMedia('(max-width: 650px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    document.querySelectorAll('[data-mobile-gallery]').forEach(gallery => {
      if (gallery.dataset.galleryReady) return;
      const rail = gallery.querySelector('[data-gallery-rail]');
      const cards = Array.from(gallery.querySelectorAll('[data-gallery-card]'));
      const dots = Array.from(gallery.querySelectorAll('[data-gallery-go]'));
      const previous = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      if (!rail || !cards.length || !previous || !next) return;
      gallery.dataset.galleryReady = 'true';
      let selected = 0, frame = null, destination = null;
      let lastWidth = null, lastMobile = null;
      // Match the CSS snap inset so the outer gutter survives button and
      // keyboard navigation, including the first and last cards.
      const leadingEdge = () => rail.getBoundingClientRect().left + (parseFloat(window.getComputedStyle(rail).scrollPaddingLeft) || 0);
      const position = card => card.getBoundingClientRect().left - leadingEdge() + rail.scrollLeft;
      function render(index) {
        selected = index;
        dots.forEach((dot, at) => dot.setAttribute('aria-pressed', String(at === index)));
        previous.disabled = index === 0;
        next.disabled = index === cards.length - 1;
      }
      function go(index, animate = true) {
        if (!mobile.matches) return;
        index = Math.max(0, Math.min(cards.length - 1, index));
        destination = index;
        render(index);
        rail.scrollTo({ left: position(cards[index]), behavior: animate && !reduced.matches ? 'smooth' : 'instant' });
        if (!animate || reduced.matches) destination = null;
      }
      rail.addEventListener('scroll', () => {
        if (!mobile.matches || frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          const left = leadingEdge();
          const distances = cards.map(card => Math.abs(card.getBoundingClientRect().left - left));
          // Keep a tapped destination selected while smooth scrolling passes
          // intermediate cards. Repeated Next taps should continue forward.
          if (destination !== null && distances[destination] > 1) return;
          destination = null;
          render(distances.indexOf(Math.min(...distances)));
        });
      }, { passive: true });
      ['pointerdown', 'wheel', 'touchstart'].forEach(name => {
        rail.addEventListener(name, () => { destination = null; }, { passive: true });
      });
      previous.addEventListener('click', () => go(selected - 1));
      next.addEventListener('click', () => go(selected + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => go(index)));
      rail.addEventListener('keydown', event => {
        if (!mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        // A guide link retains its own keyboard behavior; the rail owns these keys.
        if (event.target !== rail) return;
        event.preventDefault();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : selected + (event.key === 'ArrowRight' ? 1 : -1));
      });
      function resize() {
        const width = rail.getBoundingClientRect().width;
        // Mobile browser chrome changes height during normal page scrolling.
        // Only a width or layout change should reposition a gallery.
        if (lastWidth === width && lastMobile === mobile.matches) return;
        lastWidth = width;
        lastMobile = mobile.matches;
        rail.tabIndex = mobile.matches ? 0 : -1;
        if (mobile.matches) go(selected, false);
      }
      window.addEventListener('resize', resize, { passive: true });
      window.addEventListener('pagehide', () => {
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
      });
      resize();
    });
  }

  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();

/* ── Which store are you on, and a button that stays in reach ──────────────
   The page is rendered with data-platform="shopify" so the correct call to
   action is in the HTML, not painted in afterwards. This only ever changes
   it: on a click, on a remembered choice, on ?platform=wix, or when the
   visitor arrived from wix.com — which is them telling us where they sell.
   /wix is the page rendered already in Wix mode with no picker
   (data-platform-locked on the body): nothing switches it. */
(function platformAndStickyCta() {
  var KEY = 'mu-platform';
  var home = document.body;
  if (!document.querySelector('.conversion-home')) return;
  var locked = home.hasAttribute('data-platform-locked');

  function remembered() {
    try {
      var asked = new URLSearchParams(window.location.search).get('platform');
      if (asked === 'wix' || asked === 'shopify') return asked;
    } catch (error) {}
    try {
      var saved = localStorage.getItem(KEY);
      if (saved === 'wix' || saved === 'shopify') return saved;
    } catch (error) {}
    try {
      if (
        document.referrer &&
        /(^|\.)wix\.com$/i.test(new URL(document.referrer).hostname)
      )
        return 'wix';
    } catch (error) {}
    return null;
  }

  function apply(platform, remember) {
    home.setAttribute('data-platform', platform);
    document.querySelectorAll('a[href="/demo"], a[href^="/demo?platform="]').forEach(function(link) {
      link.setAttribute('href', '/demo?platform=' + platform);
    });
    var options = document.querySelectorAll('[data-platform-picker] [data-platform]');
    for (var i = 0; i < options.length; i++) {
      var selected = options[i].getAttribute('data-platform') === platform;
      options[i].setAttribute(
        'aria-checked',
        selected ? 'true' : 'false'
      );
      options[i].tabIndex = selected ? 0 : -1;
    }
    if (remember) {
      try {
        localStorage.setItem(KEY, platform);
      } catch (error) {}
    }
  }

  var picker = document.querySelector('[data-platform-picker]');
  if (picker && !locked) {
    picker.addEventListener('click', function (event) {
      var chosen = event.target.closest && event.target.closest('[data-platform]');
      if (chosen) apply(chosen.getAttribute('data-platform'), true);
    });
    picker.addEventListener('keydown', function (event) {
      var option = event.target.closest && event.target.closest('[role="radio"]');
      if (!option || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      var options = Array.from(picker.querySelectorAll('[role="radio"]'));
      var index = options.indexOf(option);
      var next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 :
        (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length;
      apply(options[next].getAttribute('data-platform'), true);
      options[next].focus({ preventScroll: true });
    });
  }

  /* A locked page (/wix) keeps its platform but still stamps it on the demo
     links: without ?platform= the demo falls back to Shopify. */
  var initial = locked ? home.getAttribute('data-platform') : remembered();
  if (initial) apply(initial, false);

  // Keep the bar out of the product chapters and sections with their own CTA.
  var bar = document.querySelector('[data-sticky-cta]');
  if (!bar || typeof IntersectionObserver !== 'function') return;
  var anchors = [];
  var hero = document.querySelector('.ch-hero');
  /* The closing SECTION, never its button: only one of the two buttons is in
     the layout at a time, so watching a slot would watch a display:none
     element half the time and the bar would never stand down. */
  var closing = document.querySelector('.ch-closing');
  // The examples get the whole viewport, including their navigation controls.
  var story = document.querySelector('.sms-story');
  var signup = document.querySelector('.ch-signup-journey');
  var install = document.querySelector('.ch-install-card');
  var guides = document.querySelector('.ch-resource-journey');
  var footer = document.querySelector('.site-footer');
  if (hero) anchors.push(hero);
  if (closing) anchors.push(closing);
  if (story) anchors.push(story);
  if (signup) anchors.push(signup);
  if (install) anchors.push(install);
  if (guides) anchors.push(guides);
  if (footer) anchors.push(footer);
  if (!anchors.length) return;
  /* Track WHICH anchors are on screen, not how many events have fired. A
     running count is wrong here: both anchors report once on load, so the
     one that is off screen cancels out the one that is not, and the bar
     appears over the hero it is supposed to defer to. */
  var showing = [];
  var watcher = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var at = showing.indexOf(entries[i].target);
        if (entries[i].isIntersecting) {
          if (at < 0) showing.push(entries[i].target);
        } else if (at >= 0) showing.splice(at, 1);
      }
      bar.inert = showing.length > 0;
      if (showing.length) bar.removeAttribute('data-shown');
      else bar.setAttribute('data-shown', '');
    },
    { rootMargin: '0px' }
  );
  for (var a = 0; a < anchors.length; a++) watcher.observe(anchors[a]);
})();

/* Scroll reveals (homepage.cjs REFRESH). Static content fades up a few
   pixels as it comes into view; the demos, the hero and the text-message
   story are never touched — they measure themselves and animate on their own.
   Nothing is hidden unless every condition holds: the refresh is on, the
   browser has IntersectionObserver, and the visitor has not asked for less
   motion. Only content that starts below the fold is hidden (.ch-reveal), so
   a reload or an anchor link never blinks what is already on screen. */
(function revealOnScroll() {
  var page = document.querySelector('.conversion-home.ch-refresh');
  if (!page || typeof IntersectionObserver !== 'function') return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce && reduce.matches) return;
  var view = window.innerHeight || document.documentElement.clientHeight;
  var targets = Array.prototype.slice.call(page.querySelectorAll([
    '.pe-section-heading',
    '.hf-copy',
    '.hf-next',
    '.ch-activation .ch-eyebrow',
    '.ch-activation h2',
    '.ch-setup-intro',
    '.ch-activation-steps > li',
    '.ch-activation-grid > div:first-child > .text-link',
    '.ch-install-card',
    '.ch-faq > div:first-child',
    '.ch-faq .faq-list > details',
    '.ch-guides-heading',
    '.ch-guide-card',
    '.ch-guide-checklist',
    '.ch-proof-heading',
    '.ch-closing-art > *',
    '.ch-closing .wrap > *'
  ].join(','))).filter(function (target) {
    return target.getBoundingClientRect().top >= view;
  });
  if (!targets.length) return;
  var observer = new IntersectionObserver(function (entries) {
    // Everything that arrives together cascades top to bottom, left to right.
    entries
      .filter(function (entry) { return entry.isIntersecting; })
      .sort(function (a, b) {
        return a.boundingClientRect.top - b.boundingClientRect.top ||
          a.boundingClientRect.left - b.boundingClientRect.left;
      })
      .forEach(function (entry, index) {
        entry.target.style.setProperty('--reveal-delay', Math.min(index, 5) * 70 + 'ms');
        entry.target.setAttribute('data-revealed', '');
        observer.unobserve(entry.target);
      });
  // On phones a block reveals as soon as it enters: with a bottom margin, a
  // reader who stopped with a card in the last few lines saw a blank strip.
  }, { rootMargin: window.matchMedia('(max-width: 650px)').matches ? '0px' : '0px 0px -6% 0px' });
  targets.forEach(function (target) {
    target.classList.add('ch-reveal');
    observer.observe(target);
  });
})();
